import React, {FC} from 'react';
import ReactDom from 'react-dom';
import plausibleInit from 'plausible-tracker/build/main/index';
import {createBrowserHistory} from 'history';
import {Provider as StoreProvider} from 'react-redux';
import {RouteName} from '@/app/constants/routing';
import {AnyRouteResult, RouterDeps} from '@/app/interfaces/routing';
import {getPlausiblePageUrl} from '@/app/helpers/getPlausiblePageUrl';
import {migrateLegacyConfig} from '@/app/helpers/legacyConfigMigration';
import {reportLoadPageStats} from '@/app/helpers/reportLoadPageStats';
import {webVitalsStatsReport} from '@/app/helpers/webVitalsStatsReport';
import {
	checkGeoPermissionAllowed,
	getGeoPermission,
	isGeolocationPermissionOnce,
} from '@/common/helpers/geolocation/geolocationHelper';
import {sendStats} from '@/common/helpers/stats/sendStats';
import {deepEqual} from '@/common/helpers/utils/deepEqual';
import {BackendAPI} from '@/app/services/BackendAPI';
import {createErrorInformerEmitter, ErrorInformerProvider} from '@/common/services/ErrorInformer';
import {BrowserFetcher} from '@/common/services/fetcher/BrowserFetcher';
import {HeadTaggerBrowserProvider} from '@/common/services/headTagger';
import {LocalizerProvider} from '@/common/services/localizer';
import {RemoteRefresh} from '@/common/services/RemoteRefresh';
import {BrowserRouterProvider, createRouter} from '@/common/services/router';
import {WakeUpDetector} from '@/common/services/WakeUpDetector';
import {getRouteLanguage} from '@/app/routing/getRouteLanguage';
import {createRoutes} from '@/app/routing/routes';
import {createConfiguredStore, StoreState} from '@/app/store';
import {
	fetchDefaultLocationInfoAction,
	fetchPreferencesAction,
	setDomainConfigAction,
} from '@/app/store/config/actions';
import {
	selectDefaultLocationId,
	selectHasPersonalizedPreferences,
	selectIsPreferencesReady,
	selectLanguage,
	selectMainPageLocationMode,
} from '@/app/store/config/selectors';
import {determinateCoordinates} from '@/app/store/geolocation/actions';
import {RenderConditionProvider} from '@/app/contexts/RenderConditionContext';
import {ServicesContext} from '@/app/contexts/ServicesContext';
import {OverlayContainerContext} from '@/common/contexts/OverlayContainerContext';
import {AdRefreshProvider, DfpAdProvider} from '@/app/components/common/advertising';
import {LanguageObserver} from '@/app/components/common/LanguageObserver';
import {ErrorBoundary} from '@/common/components/ErrorBoundary/ErrorBoundary';
import {localizationResources} from '@/app/locales';
import domains from '@/domains.json';

const serviceWorkerUrl = new URL('./serviceworker.ts', import.meta.url);

// eslint-disable-next-line @typescript-eslint/naming-convention
export async function initialize(interfaceName: 'desktop' | 'mobile', App: FC) {
	// Workaround for stupid the Google's "webcache" service, which directly injects a target site into the page.
	// In this case any API request can't be success finished and route can't be resolved (because url path is unexpected)
	if (location.host === 'webcache.googleusercontent.com') {
		return null as any;
	}

	/** Domain specific config for the current page domain */
	const domainConfig = domains[DOMAIN_NAME];

	// Initialize internal services
	const fetcher = new BrowserFetcher();
	const backendAPI = new BackendAPI(fetcher);
	const wakeUpDetector = new WakeUpDetector();
	wakeUpDetector.initialize();

	const errorInformerEmitter = createErrorInformerEmitter();

	// Try extract predefined from the server data
	let predefinedState: StoreState | undefined;
	const predefinedStateSource = document.getElementById('preloaded-state');
	if (predefinedStateSource) {
		try {
			predefinedState = JSON.parse(predefinedStateSource.textContent!);
		} catch (error) {
			console.error(error);
		}
	}

	// Initialize the store
	const store = createConfiguredStore({backendAPI}, predefinedState);

	store.dispatch(
		setDomainConfigAction({
			domainName: DOMAIN_NAME,
			countryId: domainConfig.countryId,
			routeKeywords: domainConfig.routerKeywords,
			primaryLanguage: domainConfig.primaryLanguage,
			supportedLanguages: domainConfig.supportedLanguages,
			bannersSet: domainConfig.bannersSet,
		}),
	);

	// Fetch user's profile if it is not being predefined
	if (!selectIsPreferencesReady(store.getState())) {
		await store.dispatch(fetchPreferencesAction());
	}

	// TODO set timeout to show an error after timeout if not rendered

	// Create a router
	const history = createBrowserHistory();
	const configuredRoutes = createRoutes(
		domainConfig.routerKeywords,
		domainConfig.primaryLanguage,
		domainConfig.supportedLanguages,
	);
	const {router, urlGenerator} = createRouter<AnyRouteResult, RouterDeps>(
		configuredRoutes,
		{store},
		{
			onError(error) {
				errorInformerEmitter.emit(error);
			},
		},
	);

	// Resolve the current route
	const resolveRoute = async (url: string, context?: Partial<RouterDeps>): Promise<AnyRouteResult> => {
		let route: AnyRouteResult;

		try {
			const resolvedRoute = await router.resolve({
				pathname: decodeURIComponent(url),
				...context,
			});
			if (!resolvedRoute) {
				throw new Error(`Empty route returned after resolve for url "${history.location.pathname}"`);
			}
			route = resolvedRoute;
		} catch (error) {
			// TODO report and review the handling method
			throw error;
		}

		// Execute redirect if required
		if (route.name === RouteName.redirect) {
			const newUrl = urlGenerator(route.to.name, route.to.params);
			history.replace(newUrl);
			return resolveRoute(newUrl);
		}

		return route;
	};

	const route = await resolveRoute(history.location.pathname, {isHydrateMode: !!predefinedState});

	// Determinate UI's language
	const language = selectLanguage(store.getState()) || getRouteLanguage(route) || domainConfig.primaryLanguage;

	// Load personalized data that is missing if the data was not transferred from the server rendering
	const preferencesFetchPromise = selectHasPersonalizedPreferences(store.getState())
		? Promise.resolve()
		: store.dispatch(fetchPreferencesAction()).then(() => undefined);

	// Load info of default location without blocking application render
	preferencesFetchPromise.then(() => {
		if (selectDefaultLocationId(store.getState())) {
			store.dispatch(fetchDefaultLocationInfoAction({language}));
		}
	});

	// Initialize geolocation determining without blocking application render
	if (checkGeoPermissionAllowed() && !isGeolocationPermissionOnce()) {
		preferencesFetchPromise
			.then(() => getGeoPermission())
			.then(permission => {
				const mainPageLocationMode = selectMainPageLocationMode(store.getState());
				if (permission.state === 'granted' && mainPageLocationMode === 'determine') {
					store.dispatch(determinateCoordinates());
				}
			});
	}

	/** Plausible analytics report service */
	const plausibleAnalytics = plausibleInit({
		domain: domainConfig.analytics.plausible.siteDomain,
		apiHost: domainConfig.analytics.plausible.reportEndpoint,
		trackLocalhost: true,
	});

	const sendPlausiblePageView = (newRoute: AnyRouteResult) => {
		plausibleAnalytics.trackPageview({url: getPlausiblePageUrl(newRoute)});
	};

	// Report to analytic systems location change
	let googleAnalyticsCreated = false;
	let prevRoute = route;
	const handleRouteChange = (newRoute: AnyRouteResult, pathname: string) => {
		// Send analytics only if the route has been changed
		if (deepEqual(prevRoute.params, newRoute.params)) {
			return;
		}

		// Google analytic
		// TODO fix this dirty workaround
		if (DOMAIN_NAME === 'sinoptik.ua') {
			if (typeof (window as any).gtag !== 'undefined' && typeof __googleAnalyticsId !== 'undefined') {
				(window as any).gtag('event', 'page_view', {
					// eslint-disable-next-line @typescript-eslint/naming-convention
					page_path: pathname,
				});
			}
		} else if (typeof ga !== 'undefined' && typeof __googleAnalyticsId !== 'undefined') {
			if (!googleAnalyticsCreated) {
				ga('create', __googleAnalyticsId);
				googleAnalyticsCreated = true;
			}
			ga('set', 'page', pathname);
			ga('send', 'pageview');
		}

		// Counter ukr net
		if (typeof __uncSend !== 'undefined') {
			__uncSend();
		}

		// Internal stats
		sendStats(`visit${pathname === '/' ? '' : pathname}`, {s: visitsSessionId});

		// Send plausible page view analytics
		sendPlausiblePageView(newRoute);

		prevRoute = newRoute;
	};

	// Render the application
	const container = document.querySelector('.root-app');
	const overlayContainer = document.querySelector('.root-overlay');
	let isHydrationMode = container!.children.length > 0;

	// TODO if hydrate mode used and error is occurred - try to initialize through a clean render and report it problem
	// TODO wrap into render error catcher and re-init clean application
	// eslint-disable-next-line @typescript-eslint/naming-convention
	const appRender = (AppComponent: FC) => {
		const renderer = isHydrationMode ? ReactDom.hydrate : ReactDom.render;

		renderer(
			<ErrorBoundary
				onError={error => {
					console.log('Render error catcher', error);
					// TODO handle an error and retry
				}}>
				<RenderConditionProvider isHydration={isHydrationMode}>
					<ServicesContext.Provider value={{backendAPI, wakeUpDetector}}>
						<OverlayContainerContext.Provider value={overlayContainer}>
							<StoreProvider store={store}>
								<BrowserRouterProvider<AnyRouteResult>
									initialRoute={route}
									router={router}
									history={history}
									urlGenerator={urlGenerator}
									routeResolver={resolveRoute}
									onChange={handleRouteChange}>
									<LocalizerProvider language={language} resources={localizationResources}>
										<LanguageObserver>
											<HeadTaggerBrowserProvider>
												<DfpAdProvider>
													<AdRefreshProvider>
														<ErrorInformerProvider emitter={errorInformerEmitter}>
															<AppComponent />
														</ErrorInformerProvider>
													</AdRefreshProvider>
												</DfpAdProvider>
											</HeadTaggerBrowserProvider>
										</LanguageObserver>
									</LocalizerProvider>
								</BrowserRouterProvider>
							</StoreProvider>
						</OverlayContainerContext.Provider>
					</ServicesContext.Provider>
				</RenderConditionProvider>
			</ErrorBoundary>,
			container,
		);
	};

	try {
		appRender(App);
	} catch (error) {
		console.log(error);
		// TODO if the hydrate is used - cleanup an retry; Notify user about an error
	}

	if ('serviceWorker' in navigator) {
		navigator.serviceWorker.register(serviceWorkerUrl, {scope: '/'});
	}

	// Extract legacy user's config (preferences) from the local storage and migrate them to the modern preferences store
	preferencesFetchPromise.then(() => {
		migrateLegacyConfig(store);
	});

	// Provide remove refresh logic after app version update
	const remoteRefresh = new RemoteRefresh();
	fetcher.onResponse.on(xhr => {
		const clientVersion = xhr.getResponseHeader('X-Client-Version');
		if (clientVersion) {
			remoteRefresh.checkClientVersion(clientVersion);
		}
	});

	remoteRefresh.onRefreshRequired.on(() => {
		history.listen(() => {
			location.reload();
		});
	});

	// Send visit stats
	const visitsSessionId = Math.round(Math.random() * 10000000000000000).toString(36);
	sendStats(
		`visit${location.pathname === '/' ? '' : location.pathname}`,
		{s: visitsSessionId, init: '1'},
		document.referrer ? {'X-Referer': document.referrer} : undefined,
	);

	// Reports page-load timing metrics
	reportLoadPageStats(performance.now(), interfaceName);

	webVitalsStatsReport(route, interfaceName);

	// Send plausible page view analytics
	sendPlausiblePageView(route);

	return {
		// eslint-disable-next-line @typescript-eslint/naming-convention
		rerender(NewApp: FC) {
			isHydrationMode = false;
			appRender(NewApp);
		},
	};
}
